import { useEffect } from 'react'
import { Platform } from 'react-native'
import {
  IQuarterProgress,
  useQuarterProgressReporter,
} from './useQuarterProgressReporter'

interface IUseSparkVideosIframeProgressParams {
  enabled: boolean
  onProgress: (params: IQuarterProgress) => void
  /** Reinicia o controle de quarter ao trocar de mídia (ex.: media.id). */
  resetKey?: string | number
}

/**
 * Escuta as mensagens `time_update` emitidas pelo player iframe do Spark Videos
 * (mesmo protocolo do embed legado em sparkvideos/.../sparkvideo.3.js:
 * `{ cmd: 'time_update', current_time, state, duration }`) e reporta o progresso
 * por quarter para a área de membros web. Só ativa no web.
 */
export function useSparkVideosIframeProgress({
  enabled,
  onProgress,
  resetKey,
}: IUseSparkVideosIframeProgressParams) {
  const report = useQuarterProgressReporter(onProgress, resetKey)

  useEffect(() => {
    if (!enabled || Platform.OS !== 'web' || typeof window === 'undefined') {
      return
    }

    const allowedOrigin = getExpectedOrigin()

    const handleMessage = (event: MessageEvent) => {
      if (allowedOrigin && event.origin && event.origin !== allowedOrigin) {
        return
      }
      const data = event?.data
      if (!data || data.cmd !== 'time_update') return
      report(Number(data.current_time), Number(data.duration))
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [enabled, report])
}

function getExpectedOrigin(): string | null {
  const base = process.env.EXPO_PUBLIC_SPARKVIDEOS_URL
  if (!base) return null
  try {
    // base pode vir como "//host", "https://host" ou "host"
    const normalized = base.startsWith('http')
      ? base
      : `https:${base.startsWith('//') ? base : `//${base}`}`
    return new URL(normalized).origin
  } catch {
    return null
  }
}
